import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, X, Trophy, Clock, Loader2, Tv } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { SorteoLiveModal } from "@/components/sorteo/SorteoLiveModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small mystical chime using WebAudio — no external asset required.
 * Two short ascending tones (~0.4s total) at a comfortable volume.
 */
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.0, now);
    masterGain.gain.linearRampToValueAtTime(0.18, now + 0.04);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
    masterGain.connect(ctx.destination);

    const tones = [
      { freq: 880, start: 0.00, dur: 0.25 },   // A5
      { freq: 1320, start: 0.12, dur: 0.30 },  // E6 (mystical 5th)
    ];
    tones.forEach((t) => {
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(t.freq, now + t.start);
      osc.connect(masterGain);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur);
    });
    // Close ctx soon to free resources
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
  } catch { /* ignore */ }
}

/**
 * Floating "Sorteo en Vivo" button.
 *
 * Visible to ALL visitors (logged in or not) when the admin has turned the
 * button ON via /api/admin/sorteo/live-button. Click → opens SorteoLiveModal
 * with TikTok Live on the left and the voting wheel on the right.
 */
export const SorteoLiveButton = () => {
  const { token } = useAuth();
  const [state, setState] = useState(null); // {button_enabled, sorteo, ...}
  const [open, setOpen] = useState(false);
  const prevStatusRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sorteo/live-state`);
      setState(data);
      // Notify users when voting opens — uses status transition (idle → voting)
      const newStatus = data?.sorteo?.status || null;
      const prev = prevStatusRef.current;
      if (prev !== "voting" && newStatus === "voting") {
        // 1. Toast
        toast.success("🎉 ¡La votación del sorteo ya está abierta! Vota tu intensidad de giro.", { duration: 6000 });
        // 2. Soft mystical chime (synthesised — no extra asset needed)
        try { playChime(); } catch { /* ignore */ }
        // 3. Auto-open the live modal so spectators don't miss the moment
        setOpen(true);
      }
      prevStatusRef.current = newStatus;
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 8s while button isn't open; live updates via SSE override.
  useEffect(() => {
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  // Realtime: refresh on any sorteo or live-button event
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useRealtimeEvents("global", token, (evt) => {
    if (!evt) return;
    if (evt.event === "sorteo_update" || evt.event === "sorteo_live_button") {
      load();
    }
  });

  if (!state?.button_enabled) return null;

  const status = state?.sorteo?.status || null;
  const dotCls =
    status === "voting" ? "bg-emerald-400 animate-pulse" :
    status === "spinning" ? "bg-amber-400 animate-pulse" :
    "bg-red-400 animate-pulse";

  const ribbonText =
    status === "voting" ? "VOTANDO" :
    status === "spinning" ? "GIRANDO" :
    "EN VIVO";

  return (
    <>
      <motion.button
        type="button"
        data-testid="sorteo-live-button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30, scale: 0.6 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ delay: 0.5, type: "spring", stiffness: 220, damping: 18 }}
        className="fixed bottom-24 left-6 z-[85] flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-red-600 via-rose-600 to-fuchsia-600 text-white font-bold text-xs uppercase tracking-widest shadow-[0_0_30px_rgba(244,63,94,0.6)] hover:shadow-[0_0_50px_rgba(244,63,94,0.9)] border border-white/20"
        title="Ver sorteo en vivo en TikTok + ruleta"
      >
        <span className={`w-2 h-2 rounded-full ${dotCls}`} />
        <Tv className="w-4 h-4" />
        <span>{ribbonText}</span>
        <Radio className="w-3.5 h-3.5 opacity-80" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <SorteoLiveModal
            key="live-modal"
            state={state}
            onClose={() => setOpen(false)}
            onRefresh={load}
          />
        )}
      </AnimatePresence>
    </>
  );
};
